import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FieldError from "../FieldError/FieldError";
import "./CityInput.css";

const normalize = (s) =>
  (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

// 🔤 Synonymes / abréviations courantes (Afrique de l'Ouest) → ville canonique.
// Permet « ouaga » → Ouagadougou, « bobo » → Bobo-Dioulasso, « abj » → Abidjan…
const SYNONYMS = {
  ouaga: "Ouagadougou", ouag: "Ouagadougou",
  bobo: "Bobo-Dioulasso",
  abj: "Abidjan", abidj: "Abidjan",
  yamou: "Yamoussoukro", yakro: "Yamoussoukro",
  ako: "Abomey-Calavi",
  cotonou: "Cotonou", coto: "Cotonou",
  lome: "Lomé",
  bko: "Bamako", bamako: "Bamako",
  niamey: "Niamey", nia: "Niamey",
  dla: "Douala", douala: "Douala",
};

// 🕘 Historique de recherche des villes (localStorage, max 6, dédupliqué).
const RECENT_KEY = "gowele_recent_cities";
const getRecentCities = () => {
  try { const v = JSON.parse(localStorage.getItem(RECENT_KEY)); return Array.isArray(v) ? v : []; }
  catch { return []; }
};
export const recordRecentCity = (city) => {
  if (!city || !city.trim()) return;
  try {
    const prev = getRecentCities().filter((c) => normalize(c) !== normalize(city));
    localStorage.setItem(RECENT_KEY, JSON.stringify([city.trim(), ...prev].slice(0, 6)));
  } catch { /* quota / mode privé → ignore */ }
};

// Distance de Levenshtein (tolérance aux fautes de frappe : "yamossoukro" → "yamoussoukro").
const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 0; i < a.length; i++) {
    const curr = [i + 1];
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      curr[j + 1] = Math.min(curr[j] + 1, prev[j + 1] + 1, prev[j] + cost);
    }
    prev = curr;
  }
  return prev[b.length];
};

const CityInput = ({ label, name, placeholder, value = "", cities = [], onChange, required, error }) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);

  const nv = normalize(value);

  // 0) Historique : champ vide + focus → propositions récentes (limitées aux villes connues si dispo).
  const isHistory = open && value.length === 0;
  const recent = isHistory
    ? getRecentCities()
        .filter((c) => cities.length === 0 || cities.some((city) => normalize(city) === normalize(c)))
        .slice(0, 6)
    : [];

  // 1) Correspondances par sous-chaîne (comportement standard de l'autocomplétion).
  const substringMatches = open && value.length >= 1
    ? cities
        .filter((c) => normalize(c).includes(nv))
        .sort((a, b) => {
          const startA = normalize(a).startsWith(nv);
          const startB = normalize(b).startsWith(nv);
          if (startA !== startB) return startA ? -1 : 1;
          return a.localeCompare(b, "fr");
        })
        .slice(0, 8)
    : [];

  // 1b) Synonymes / abréviations : « ouaga » → Ouagadougou (si la ville canonique existe).
  if (open && nv.length >= 2) {
    Object.entries(SYNONYMS).forEach(([alias, canonical]) => {
      if (alias.startsWith(nv) || nv.startsWith(alias)) {
        const match = cities.find((c) => normalize(c) === normalize(canonical)) || canonical;
        if (!substringMatches.some((c) => normalize(c) === normalize(match))) {
          substringMatches.unshift(match);
        }
      }
    });
  }

  // 2) Repli « Vouliez-vous dire ? » : si aucune correspondance directe et saisie ≥ 3 caractères,
  //    proposer les villes les plus proches par distance d'édition (tolère 1-2 fautes de frappe).
  const isFuzzy = open && substringMatches.length === 0 && nv.length >= 3;
  const suggestions = isHistory
    ? recent
    : isFuzzy
    ? cities
        .map((c) => ({ city: c, dist: levenshtein(nv, normalize(c)) }))
        .filter(({ dist, city }) => dist <= Math.max(2, Math.floor(normalize(city).length / 4)))
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 5)
        .map(({ city }) => city)
    : substringMatches.slice(0, 8);

  const handleBlur = () => setTimeout(() => { setOpen(false); setActiveIndex(-1); }, 150);

  const handleChange = (e) => {
    onChange({ name, value: e.target.value });
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleSelect = (city) => {
    onChange({ name, value: city });
    recordRecentCity(city);
    setOpen(false);
    setActiveIndex(-1);
  };

  // ⌨️ Navigation clavier : ↑/↓ pour parcourir, Entrée pour choisir, Échap pour fermer.
  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    onChange({ name, value: "" });
  };

  const errorId = error ? `${name}-error` : undefined;

  return (
    <div className={`city-input form-group${error ? ' field--has-error' : ''}`} ref={ref}>
      {label && (
        <label className="form-label" htmlFor={name}>
          {label}{required && <span className="required-star"> *</span>}
        </label>
      )}
      <div className="city-input__field">
        <input
          className={`form-input${error ? ' error' : ''}`}
          id={name}
          type="text"
          name={name}
          placeholder={placeholder}
          value={value}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={errorId}
        />
        {value && (
          <button
            type="button"
            className="city-input__clear"
            onMouseDown={handleClear}
            tabIndex={-1}
            aria-label={t('ui.clear', 'Effacer')}
          >
            ×
          </button>
        )}
      </div>
      {suggestions.length > 0 && (
        <ul className="city-input__suggestions" role="listbox">
          {isHistory && (
            <li className="city-input__hint" aria-hidden="true">
              {t('ui.recentSearches', 'Recherches récentes')}
            </li>
          )}
          {isFuzzy && (
            <li className="city-input__hint" aria-hidden="true">
              {t('ui.didYouMean', 'Vouliez-vous dire ?')}
            </li>
          )}
          {suggestions.map((city, idx) => (
            <li
              key={city}
              className={`city-input__option${idx === activeIndex ? ' city-input__option--active' : ''}`}
              role="option"
              aria-selected={idx === activeIndex}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={() => handleSelect(city)}
            >
              <span className="city-input__pin">{isHistory ? '🕘' : '📍'}</span>
              {city}
            </li>
          ))}
        </ul>
      )}
      {error && <FieldError message={error} fieldId={name} />}
    </div>
  );
};

export default CityInput;
